import React, { useState, useEffect } from "react";
import { Rocket, CheckCircle2, ExternalLink, X as XIcon, AlertTriangle } from "lucide-react";

/**
 * DeploySyncChecklist — top-of-admin reminder that explains the
 * preview ↔ deployed split and lays out the 3-step "catch up production"
 * flow. Dismissible (localStorage), reappears after each version bump.
 */
const VERSION = "iter23"; // bump to re-show on next iteration

export default function DeploySyncChecklist() {
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setDismissed(localStorage.getItem("cc_deploy_checklist_dismissed") === VERSION);
  }, []);

  const dismiss = () => {
    localStorage.setItem("cc_deploy_checklist_dismissed", VERSION);
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <div
      className="border-2 border-[#0369a1] bg-gradient-to-br from-[#f0f9ff] to-white p-6 mb-8 relative"
      data-testid="deploy-sync-checklist"
    >
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 p-1.5 hover:bg-[#0369a1]/10 rounded"
        data-testid="deploy-sync-dismiss"
        aria-label="Dismiss checklist"
      >
        <XIcon className="w-4 h-4 text-[#0369a1]" strokeWidth={2.5} />
      </button>

      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="eyebrow text-[#0369a1] flex items-center gap-1.5">
            <Rocket className="w-3 h-3" /> Deploy &amp; sync checklist
          </div>
          <h3 className="font-display text-xl mt-1">
            Get your production site caught up
          </h3>
          <p className="text-sm text-[#444] leading-snug mt-2 max-w-[700px]">
            Preview &amp; production have <strong>separate databases</strong>. Anything
            we&apos;ve done in preview (new campsites, Llyn Gwynant fix, sort order)
            won&apos;t appear on your live site until you do these 3 steps:
          </p>
        </div>
      </div>

      <ol className="mt-5 space-y-4 max-w-[750px]">
        <li className="flex items-start gap-3" data-testid="deploy-step-1">
          <div className="font-display text-2xl text-[#0369a1] mono w-8 flex-shrink-0">1</div>
          <div>
            <div className="font-display text-base">
              Click <strong className="text-[#ff4500]">Deploy app</strong> in the Emergent chat header
            </div>
            <p className="text-sm text-[#555] mt-1">
              Pushes today&apos;s code (520 fix, &ldquo;Bookable now&rdquo; filter,
              direct-book fallback, pagination, broken-link reports) to your live URL.
              Takes ~2 minutes.
            </p>
          </div>
        </li>

        <li className="flex items-start gap-3" data-testid="deploy-step-2">
          <div className="font-display text-2xl text-[#0369a1] mono w-8 flex-shrink-0">2</div>
          <div>
            <div className="font-display text-base">
              Click <strong>Run Bootstrap</strong> on your <em>deployed</em> /admin page
            </div>
            <p className="text-sm text-[#555] mt-1">
              Fixes Llyn Gwynant, flags year-round campsites, computes
              <code className="mono text-xs px-1 bg-[#eef] rounded mx-1">book_rank</code>
              for sort order. Now returns in &lt;1s (no more 520). Wait ~2 min for
              background jobs, then re-run to see updated counts.
            </p>
          </div>
        </li>

        <li className="flex items-start gap-3" data-testid="deploy-step-3">
          <div className="font-display text-2xl text-[#0369a1] mono w-8 flex-shrink-0">3</div>
          <div>
            <div className="font-display text-base">
              Run <strong>Bulk-ingest from Pitchup</strong> repeatedly
            </div>
            <p className="text-sm text-[#555] mt-1">
              Each click adds 50–200 new bookable Pitchup sites (Nominatim
              rate-limited, ~1s/site). Click 10–20 times to catch up to ~5,000
              sites. Idempotent &mdash; re-running skips dupes.
            </p>
          </div>
        </li>
      </ol>

      <div className="mt-5 border hairline bg-[#fff5ef] p-3 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-[#ff4500] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
        <div className="text-xs mono text-[#444]">
          <strong>Why two databases?</strong> Preview lets us experiment safely
          without touching real users. Deploy promotes <em>code</em>, not data.
          That&apos;s why ingest needs to run on each environment separately.
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <a
          href="https://docs.emergent.com/deploy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs mono text-[#0369a1] hover:text-[#075985] inline-flex items-center gap-1 underline decoration-dotted underline-offset-4"
          data-testid="deploy-docs-link"
        >
          Emergent deploy docs <ExternalLink className="w-3 h-3" />
        </a>
        <button
          onClick={dismiss}
          className="text-xs mono text-[#666] hover:text-[#222] underline decoration-dotted underline-offset-4 inline-flex items-center gap-1"
          data-testid="deploy-sync-got-it"
        >
          <CheckCircle2 className="w-3 h-3" /> Got it &mdash; hide this
        </button>
      </div>
    </div>
  );
}
